import type { SoftphoneChosenPresence, SoftphoneState, StartSoftphoneCall } from '@lia/contracts';
import type { TransferChoice } from '@lia/ui';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { api, errorMessage } from '../api.js';
import { useApiQuery } from '../hooks.js';
import { wk } from '../query.js';
import { browserGetUserMedia, requestMicrophone, type GetUserMedia, type SoftphoneAdapter, type SoftphoneAdapterEvent } from './adapter.js';
import { createRingTone, type RingTone } from './ring-tone.js';
import { createTelnyxAdapter } from './telnyx-adapter.js';

// Advisor softphone controller (plan B3). Holds the server state (getSoftphoneState), the presence heartbeat and the
// WebRTC adapter lifecycle. LIA places and bridges every call with Telnyx Call Control; this browser is only the
// advisor's audio leg:
//  • outbound: POST startSoftphoneCall → LIA first rings this softphone (an inbound invite) — auto-answered while a
//    request placed here is pending (≤ 45 s) — then dials the prospect and bridges on answer;
//  • inbound: a ring-group invite rings here (in-app card + tone + optional browser notification) until answered,
//    rejected or taken by someone else.
// Truth about the call comes from the server (`call.state`); muted/held are local to the SDK.

export type SoftphoneSdkStatus = 'idle' | 'connecting' | 'ready' | 'disconnected' | 'error';

export const AUTO_ANSWER_WINDOW_MS = 45_000;
export const HEARTBEAT_MS = 25_000;
const POLL_ACTIVE_MS = 3_000;
const POLL_IDLE_MS = 20_000;
const READY_TIMEOUT_MS = 20_000;

export interface SoftphoneIncoming {
  callId: string;
  remoteNumber: string | null;
}

export interface SoftphoneController {
  state: SoftphoneState | undefined;
  /** The advisor has a seat and nothing blocks it (calling still needs the SDK connected; `call` connects first). */
  seatUsable: boolean;
  sdk: SoftphoneSdkStatus;
  /** Latest connection / microphone error (Spanish). */
  error: string | null;
  /** Latest failed action (call, transfer, presence…), Spanish. */
  actionError: string | null;
  incoming: SoftphoneIncoming | null;
  muted: boolean;
  held: boolean;
  placing: boolean;
  /** An outbound request is waiting for LIA to ring this softphone. */
  pendingOutbound: boolean;
  notifications: { supported: boolean; permission: NotificationPermission | 'unsupported'; enable: () => Promise<void> };
  connect: () => Promise<boolean>;
  disconnect: () => Promise<void>;
  call: (input: StartSoftphoneCall) => Promise<boolean>;
  answer: () => Promise<void>;
  reject: () => Promise<void>;
  hangup: () => Promise<void>;
  setMuted: (muted: boolean) => void;
  setHeld: (held: boolean) => Promise<void>;
  sendDtmf: (digit: string) => void;
  transfer: (choice: TransferChoice) => Promise<string | null>;
  setPresence: (presence: SoftphoneChosenPresence) => Promise<void>;
  clearActionError: () => void;
}

const SoftphoneContext = React.createContext<SoftphoneController | null>(null);

/** The softphone controller, or null outside the provider (e.g. compact windows) or for members below advisor. */
export function useSoftphone(): SoftphoneController | null {
  return React.useContext(SoftphoneContext);
}

export interface SoftphoneProviderProps {
  workspaceId: string;
  /** Members with role ≥ advisor. When false nothing is fetched and children render without a controller. */
  enabled: boolean;
  children: React.ReactNode;
  createAdapter?: () => SoftphoneAdapter;
  /** null = the browser cannot capture audio; undefined = use the browser's. */
  getUserMedia?: GetUserMedia | null;
  ringTone?: RingTone;
}

const liveCall = (s: SoftphoneState | undefined) => !!s?.call && s.call.state !== 'ended';

function notificationApi(): typeof Notification | null {
  return typeof window !== 'undefined' && 'Notification' in window ? window.Notification : null;
}

export function SoftphoneProvider({ enabled, children, ...rest }: SoftphoneProviderProps) {
  if (!enabled) return <>{children}</>;
  return <SoftphoneControllerProvider {...rest}>{children}</SoftphoneControllerProvider>;
}

function SoftphoneControllerProvider({ workspaceId, children, createAdapter, getUserMedia, ringTone }: Omit<SoftphoneProviderProps, 'enabled'>) {
  const qc = useQueryClient();
  const key = wk.softphone(workspaceId);
  const params = React.useMemo(() => ({ workspaceId }), [workspaceId]);
  const [sdk, setSdk] = React.useState<SoftphoneSdkStatus>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [incoming, setIncoming] = React.useState<SoftphoneIncoming | null>(null);
  const [muted, setMutedState] = React.useState(false);
  const [held, setHeldState] = React.useState(false);
  const [placing, setPlacing] = React.useState(false);
  const [pendingOutboundAt, setPendingOutboundAt] = React.useState<number | null>(null);
  const [notifPermission, setNotifPermission] = React.useState<NotificationPermission | 'unsupported'>(() => notificationApi()?.permission ?? 'unsupported');

  const busy = liveCall(qc.getQueryData<SoftphoneState>(key)) || !!incoming || pendingOutboundAt !== null;
  const q = useApiQuery(key, 'getSoftphoneState', { params }, { retry: false, staleTime: 2_000, refetchInterval: busy ? POLL_ACTIVE_MS : POLL_IDLE_MS });
  const state = q.data;
  const seatUsable = !!state && state.seat.enabled && !state.seat.blocker;

  const adapterRef = React.useRef<SoftphoneAdapter | null>(null);
  const unsubscribeRef = React.useRef<(() => void) | null>(null);
  const readyWaiter = React.useRef<((ok: boolean) => void) | null>(null);
  const closingRef = React.useRef(false);
  const pendingRef = React.useRef<number | null>(null);
  const ringRef = React.useRef<RingTone | null>(ringTone ?? null);
  const ring = React.useCallback(() => (ringRef.current ??= createRingTone()), []);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  const setServerState = React.useCallback((s: SoftphoneState) => qc.setQueryData(key, s), [qc, key]);
  const refresh = React.useCallback(() => void qc.invalidateQueries({ queryKey: key }), [qc, key]);
  const setPending = (at: number | null) => {
    pendingRef.current = at;
    setPendingOutboundAt(at);
  };

  const notify = (remoteNumber: string | null) => {
    const N = notificationApi();
    if (!N || N.permission !== 'granted') return;
    const c = stateRef.current?.call;
    const who = c?.contactName ?? c?.counterpartE164 ?? remoteNumber ?? 'Número desconocido';
    try {
      new N('Llamada entrante', { body: who, tag: 'lia-softphone' });
    } catch {
      // Notifications are optional.
    }
  };

  // Latest event handler behind a stable ref, so the adapter subscription never sees stale state.
  const onEventRef = React.useRef<(e: SoftphoneAdapterEvent) => void>(() => undefined);
  onEventRef.current = (e: SoftphoneAdapterEvent) => {
    switch (e.type) {
      case 'ready':
        setSdk('ready');
        setError(null);
        readyWaiter.current?.(true);
        readyWaiter.current = null;
        return;
      case 'error':
        setError(e.message);
        if (readyWaiter.current) {
          setSdk('error');
          readyWaiter.current(false);
          readyWaiter.current = null;
        }
        return;
      case 'disconnected':
        if (closingRef.current) return;
        setSdk('disconnected');
        setError('Se perdió la conexión del softphone. Vuelve a conectarlo para recibir y hacer llamadas.');
        setIncoming(null);
        ring().stop();
        readyWaiter.current?.(false);
        readyWaiter.current = null;
        return;
      case 'incoming': {
        const at = pendingRef.current;
        if (at !== null && Date.now() - at <= AUTO_ANSWER_WINDOW_MS) {
          // LIA's own first leg of the outbound call this advisor just placed: answer it without ringing.
          setPending(null);
          void adapterRef.current?.answer().catch(() => setActionError('No se pudo conectar el audio de la llamada.'));
          refresh();
          return;
        }
        setIncoming({ callId: e.callId, remoteNumber: e.remoteNumber });
        ring().start();
        notify(e.remoteNumber);
        refresh();
        return;
      }
      case 'call':
        if (e.state === 'held') setHeldState(true);
        if (e.state === 'active') setHeldState(false);
        if (e.state === 'hangup') {
          setIncoming((cur) => (cur && cur.callId === e.callId ? null : cur));
          ring().stop();
          setMutedState(false);
          setHeldState(false);
        }
        refresh();
        return;
    }
  };

  const adapter = React.useCallback((): SoftphoneAdapter => {
    if (!adapterRef.current) {
      const a = (createAdapter ?? (() => createTelnyxAdapter()))();
      adapterRef.current = a;
      unsubscribeRef.current = a.subscribe((e) => onEventRef.current(e));
    }
    return adapterRef.current;
  }, [createAdapter]);

  // Heartbeat while the page is open (and immediately when the SDK registration changes).
  const registered = sdk === 'ready';
  const seatEnabled = !!state?.seat.enabled;
  React.useEffect(() => {
    if (!seatEnabled) return;
    let alive = true;
    const beat = () =>
      void api('softphoneHeartbeat', { params, body: { registered } })
        .then((s) => alive && setServerState(s))
        .catch(() => undefined);
    beat();
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [seatEnabled, registered, params, setServerState]);

  // Expire the auto-answer window.
  React.useEffect(() => {
    if (pendingOutboundAt === null) return;
    const id = setTimeout(() => setPending(null), Math.max(0, pendingOutboundAt + AUTO_ANSWER_WINDOW_MS - Date.now()));
    return () => clearTimeout(id);
  }, [pendingOutboundAt]);

  // Stop ringing once the server no longer offers this call to us (someone else answered, caller hung up).
  const serverCallState = state?.call?.state;
  React.useEffect(() => {
    if (incoming && serverCallState && serverCallState !== 'ringing') {
      // The SDK hangup event normally arrives too; this is the server-side safety net.
      if (serverCallState === 'ended') {
        setIncoming(null);
        ring().stop();
      }
    }
  }, [incoming, serverCallState, ring]);

  // Release everything on unmount.
  React.useEffect(
    () => () => {
      closingRef.current = true;
      ringRef.current?.stop();
      unsubscribeRef.current?.();
      void adapterRef.current?.disconnect().catch(() => undefined);
      adapterRef.current = null;
    },
    [],
  );

  const connect = React.useCallback(async (): Promise<boolean> => {
    const s = stateRef.current;
    if (!s || !s.seat.enabled || s.seat.blocker) return false;
    setSdk('connecting');
    setError(null);
    const mic = await requestMicrophone(getUserMedia === undefined ? browserGetUserMedia() : getUserMedia);
    if (!mic.ok) {
      setSdk('error');
      setError(mic.message);
      return false;
    }
    let loginToken: string;
    try {
      loginToken = (await api('mintSoftphoneToken', { params })).loginToken;
    } catch (err) {
      setSdk('error');
      setError(errorMessage(err));
      return false;
    }
    const a = adapter();
    const ready = new Promise<boolean>((resolve) => {
      readyWaiter.current = resolve;
    });
    closingRef.current = false;
    try {
      await a.connect(loginToken);
    } catch {
      readyWaiter.current = null;
      setSdk('error');
      setError('No se pudo conectar el softphone con Telnyx.');
      return false;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(false), READY_TIMEOUT_MS);
    });
    const ok = await Promise.race([ready, timeout]);
    clearTimeout(timer);
    if (!ok && readyWaiter.current) {
      readyWaiter.current = null;
      setSdk('error');
      setError('Telnyx no confirmó el registro del softphone. Intenta de nuevo.');
    }
    return ok;
  }, [adapter, getUserMedia, params]);

  const disconnect = React.useCallback(async () => {
    closingRef.current = true;
    ring().stop();
    setIncoming(null);
    await adapterRef.current?.disconnect().catch(() => undefined);
    setSdk('idle');
    setError(null);
  }, [ring]);

  const call = React.useCallback(
    async (input: StartSoftphoneCall): Promise<boolean> => {
      setActionError(null);
      if (sdk !== 'ready') {
        const ok = await connect();
        if (!ok) return false;
      }
      setPlacing(true);
      setPending(Date.now());
      try {
        setServerState(await api('startSoftphoneCall', { params, body: input }));
        return true;
      } catch (err) {
        setPending(null);
        setActionError(errorMessage(err));
        return false;
      } finally {
        setPlacing(false);
      }
    },
    [sdk, connect, params, setServerState],
  );

  const answer = React.useCallback(async () => {
    ring().stop();
    try {
      await adapterRef.current?.answer();
      setIncoming(null);
    } catch {
      setActionError('No se pudo contestar la llamada.');
    }
    refresh();
  }, [ring, refresh]);

  const reject = React.useCallback(async () => {
    ring().stop();
    setIncoming(null);
    await adapterRef.current?.hangup().catch(() => undefined);
    refresh();
  }, [ring, refresh]);

  const hangup = React.useCallback(async () => {
    setPending(null);
    await adapterRef.current?.hangup().catch(() => undefined);
    const c = stateRef.current?.call;
    if (c && c.state !== 'ended') {
      try {
        setServerState(await api('hangupSoftphoneCall', { params: { ...params, callId: c.callId } }));
      } catch (err) {
        setActionError(errorMessage(err));
      }
    }
  }, [params, setServerState]);

  const setMuted = React.useCallback((m: boolean) => {
    adapterRef.current?.setMuted(m);
    setMutedState(m);
  }, []);

  const setHeld = React.useCallback(async (h: boolean) => {
    try {
      await adapterRef.current?.setHeld(h);
      setHeldState(h);
    } catch {
      setActionError(h ? 'No se pudo poner la llamada en espera.' : 'No se pudo reanudar la llamada.');
    }
  }, []);

  const sendDtmf = React.useCallback((d: string) => adapterRef.current?.sendDtmf(d), []);

  const transfer = React.useCallback(
    async (choice: TransferChoice): Promise<string | null> => {
      const c = stateRef.current?.call;
      if (!c) return 'No hay una llamada que transferir.';
      try {
        setServerState(await api('transferSoftphoneCall', { params: { ...params, callId: c.callId }, body: choice }));
        return null;
      } catch (err) {
        return errorMessage(err);
      }
    },
    [params, setServerState],
  );

  const setPresence = React.useCallback(
    async (presence: SoftphoneChosenPresence) => {
      try {
        setServerState(await api('setSoftphonePresence', { params, body: { presence } }));
      } catch (err) {
        setActionError(errorMessage(err));
      }
    },
    [params, setServerState],
  );

  const enableNotifications = React.useCallback(async () => {
    const N = notificationApi();
    if (!N) return;
    try {
      setNotifPermission(await N.requestPermission());
    } catch {
      setNotifPermission(N.permission);
    }
  }, []);

  const value: SoftphoneController = {
    state,
    seatUsable,
    sdk,
    error,
    actionError,
    incoming,
    muted,
    held,
    placing,
    pendingOutbound: pendingOutboundAt !== null,
    notifications: { supported: notifPermission !== 'unsupported', permission: notifPermission, enable: enableNotifications },
    connect,
    disconnect,
    call,
    answer,
    reject,
    hangup,
    setMuted,
    setHeld,
    sendDtmf,
    transfer,
    setPresence,
    clearActionError: () => setActionError(null),
  };
  return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
}
